import { t } from "i18next";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Club } from "../../../../interfaces/club";
import { Athlete } from "../../../../interfaces/athlete";
import { getAthlete } from "../../../../services/athlete";
import { CoachAthleteEdit } from "./athlete-edit";
import moment from 'moment';
import { getClubOptions } from "../../../../services/club";
import avatar from "../../../../assets/images/avatar.svg";
import { CoachAthleteMeasurement } from "./athlete-measurement";
import { getTeamOptions } from "../../../../services/team";
import { Link } from "react-router-dom";
import { CoachAthletePerformance } from "./athlete-performance";
import { createBulkAthletePerformance, getAthlete as refreshAthletes } from "../../../../services/athlete";
import { useKpiSettings } from "../../../../contexts/KpiSettingsContext";
import { timeToSeconds, formatKpiValue } from "../../../../util/time-format";

export function CoachAthleteList() {
    const { getKpiLabelWithUnit, getActiveKpis, kpiSettingsLoaded } = useKpiSettings();
    const [athletes, setAthletes] = useState<Athlete[]>([]);
    const [clubOptions, setClubOptions] = useState<any[]>([]);
    const [teamOptions, setTeamOptions] = useState<any[]>([]);
    const [showEdit, setShowEdit] = useState(false);
    const [showMeasurement, setShowMeasurement] = useState(false);
    const [showPerformance, setShowPerformance] = useState(false);
    const [selectedId, setSelectedId] = useState(0);
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [quickAddMode, setQuickAddMode] = useState(false);
    const [performanceDate, setPerformanceDate] = useState(moment().format('YYYY-MM-DD'));
    const [performanceInputs, setPerformanceInputs] = useState<{[athleteId: number]: Record<string, string | number>}>({});
    const [searchParams, setSearchParams] = useState({
        search: '',
        club: '',
        team: '',
        sortbyfield: 'updated_at',
        sortbytype: 'DESC'
    });

    useEffect(() => {
        getClubOptions()
            .then(response => {
                setClubOptions(response.data);
            })
            .catch(error => {
                console.log(error);
            });
        getAthlete('', '', '', '', 'updated_at', 'DESC', 1, 12)
            .then(response => {
                const athletesData = response.data;
                setAthletes(athletesData);
                setHasMore(athletesData.length === 12);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    // Debug: Log KPI settings status
    useEffect(() => {
    }, [kpiSettingsLoaded, getActiveKpis]);

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const s = event.target.search.value;
        const club = event.target.club.value;
        let team = '';
        if (event.target.team !== undefined) {
            team = event.target.team.value;
        }
        const sortbyfield = event.target.sortbyfield.value;
        const sortbytype = event.target.sortbytype.value;

        setSearchParams({ search: s, club, team, sortbyfield, sortbytype });
        setCurrentPage(1);

        getAthlete(s, club, team, '', sortbyfield, sortbytype, 1, 12)
            .then(response => {
                const athletesData = response.data;
                setAthletes(athletesData);
                setHasMore(athletesData.length === 12);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleLoadMore = (event: any) => {
        event.preventDefault();
        const nextPage = currentPage + 1;

        getAthlete(
            searchParams.search,
            searchParams.club,
            searchParams.team,
            '',
            searchParams.sortbyfield,
            searchParams.sortbytype,
            nextPage,
            12
        )
            .then(response => {
                const newAthletes = response.data;
                setAthletes([...athletes, ...newAthletes]);
                setCurrentPage(nextPage);
                setHasMore(newAthletes.length === 12);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowEdit(true);
    }

    const handleEditOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        const newAthletes = athletes.map((m: Athlete) => {
            if (m.id == changedAthlete.id) {
                return changedAthlete;
            } else {
                return m;
            }
        });
        setAthletes(newAthletes);
        setShowEdit(false);
    }

    const handleEditOnClose = () => {
        setSelectedId(0);
        setShowEdit(false);
    }

    const handleMeasurement = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowMeasurement(true);
    }

    const handleMeasurementOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        setShowMeasurement(false);
    }

    const handleMeasurementOnClose = () => {
        setSelectedId(0);
        setShowMeasurement(false);
    }

    const handlePerformance = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowPerformance(true);
    }

    const handlePerformanceOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        setShowPerformance(false);
    }

    const handlePerformanceOnClose = () => {
        setSelectedId(0);
        setShowPerformance(false);
    }

    const handleClubChange = (event: any) => {
        if (event.target.value.length > 0) {
            getTeamOptions(event?.target.value)
                .then((response: any) => {
                    setTeamOptions(response.data.map((t: any) => {
                        return {
                            value: t.id,
                            label: t.name
                        }
                    }))
                })
                .catch((error: any) => {
                    console.log(error);
                });
        } else {
            setTeamOptions([]);
        }
    }

    const handleViewModeToggle = () => {
        const newMode = viewMode === 'cards' ? 'table' : 'cards';
        setViewMode(newMode);

        if (newMode === 'table') {
            // Cargar todos los atletas sin paginación para la vista de tabla
            getAthlete(
                searchParams.search,
                searchParams.club,
                searchParams.team,
                '',
                searchParams.sortbyfield,
                searchParams.sortbytype,
                1,
                999999
            )
                .then(response => {
                    setAthletes(response.data);
                    setHasMore(false);
                })
                .catch(error => {
                    console.log(error);
                });
        } else {
            // Volver a modo cards con paginación
            setCurrentPage(1);
            getAthlete(
                searchParams.search,
                searchParams.club,
                searchParams.team,
                '',
                searchParams.sortbyfield,
                searchParams.sortbytype,
                1,
                12
            )
                .then(response => {
                    const athletesData = response.data;
                    setAthletes(athletesData);
                    setHasMore(athletesData.length === 12);
                })
                .catch(error => {
                    console.log(error);
                });
        }
    }

    const calculateAveragePerformance = (performances: any[], kpiName: string, dataType: 'decimal' | 'time' = 'decimal') => {
        if (!performances || performances.length === 0) return '-';

        // Filtrar solo valores válidos del KPI específico
        const validValues = performances
            .map(p => {
                // Soporte para formato nuevo (kpis object) y antiguo (perf1-4)
                if (p.kpis && p.kpis[kpiName] !== undefined) {
                    return parseFloat(p.kpis[kpiName].toString()) || 0;
                } else if (p[kpiName] !== undefined) {
                    // Fallback para formato antiguo
                    return parseFloat(p[kpiName].toString()) || 0;
                }
                return 0;
            })
            .filter(value => value > 0);

        if (validValues.length === 0) return '-';

        const sum = validValues.reduce((acc, value) => acc + value, 0);
        const avg = sum / validValues.length;

        // Format the average value according to the KPI data type
        return formatKpiValue(avg, dataType === 'time' ? 'time' : 'decimal');
    }

    const handleToggleQuickAdd = () => {
        setQuickAddMode(!quickAddMode);
        if (!quickAddMode) {
            setPerformanceDate(moment().format('YYYY-MM-DD'));
            setPerformanceInputs({});
        }
    }

    const handlePerformanceInputChange = (athleteId: number, kpiName: string, value: string) => {
        setPerformanceInputs(prev => {
            const athleteKpis = { ...prev[athleteId] };

            if (value === '' || value === null || value === undefined) {
                // Remove the KPI if value is empty
                delete athleteKpis[kpiName];
            } else {
                // Store value as string (for time) or as is
                athleteKpis[kpiName] = value;
            }

            return {
                ...prev,
                [athleteId]: athleteKpis
            };
        });
    }

    const handleSavePerformances = async () => {
        // Get active KPIs to know which are time format
        const activeKpis = getActiveKpis();
        const kpiFormats = Object.fromEntries(
            activeKpis.map(kpi => [kpi.kpi_name, kpi.data_type])
        );

        // Filtrar solo atletas con al menos un valor de KPI
        const athletesPerformance = Object.entries(performanceInputs)
            .filter(([_, kpis]) => {
                return Object.keys(kpis).length > 0;
            })
            .map(([athleteId, kpis]) => {
                // Convert KPI values: time format to seconds, decimal stays as is
                const convertedKpis: Record<string, number> = {};
                Object.entries(kpis).forEach(([kpiName, value]) => {
                    if (kpiFormats[kpiName] === 'time' && typeof value === 'string') {
                        convertedKpis[kpiName] = timeToSeconds(value);
                    } else {
                        convertedKpis[kpiName] = parseFloat(value as string);
                    }
                });

                return {
                    athlete_id: parseInt(athleteId),
                    kpis: convertedKpis
                };
            });

        if (athletesPerformance.length === 0) {
            alert(t("No performance data to save"));
            return;
        }

        const body = {
            performance_timestamp: performanceDate + ' 00:00:00',
            athletes_performance: athletesPerformance
        };

        try {
            const response = await createBulkAthletePerformance(body);
            if (response.data.status == 1) {
                alert(t("Performance data saved successfully"));
                setQuickAddMode(false);
                setPerformanceInputs({});

                // Recargar los atletas para reflejar los nuevos datos
                refreshAthletes(
                    searchParams.search,
                    searchParams.club,
                    searchParams.team,
                    '',
                    searchParams.sortbyfield,
                    searchParams.sortbytype,
                    1,
                    999999
                ).then(response => {
                    setAthletes(response.data);
                }).catch(error => {
                    console.log(error);
                });
            } else {
                alert(t("Error adding performance data. Please try again."));
            }
        } catch (error) {
            console.log(error);
            alert(t("Error adding performance data. Please try again."));
        }
    }

    const handleCancelPerformances = () => {
        setPerformanceInputs({});
        setQuickAddMode(false);
        setPerformanceDate(moment().format('YYYY-MM-DD'));
    }

    return (
        <div className="athletes-list-container">
            <h1>
                { t('athletes')}
            </h1>
            <div className="filters-container">
                <Form onSubmit={ handleSearchSubmit }>
                    <Form.Group className="field-search" controlId="search">
                        <Form.Control type="text" placeholder={ t("Write to search") } />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="club_id">
                        <Form.Select aria-label={ t("club") } name="club" onChange={ handleClubChange }>
                            <option value="">{ t("select_club") }</option>
                            { clubOptions.map((c: Club) => {
                                return <option key={ 'club-' + c.id } value={ c.id }>{ c.name }</option>;
                            })}
                        </Form.Select>
                    </Form.Group>
                    { teamOptions.length > 0 &&
                        <Form.Group className="mb-3" controlId="team">
                            <Form.Select aria-label={ t("team") } name="team">
                                <option value="">{ t("select_team") }</option>
                                { teamOptions.map((t: any) => {
                                    return <option key={ 'team-' + t.value } value={ t.value }>{ t.label }</option>;
                                })}
                            </Form.Select>
                        </Form.Group>
                    }
                    <Form.Group className="mb-3" controlId="sortbyfield">
                        <Form.Select aria-label={ t("Sort by") } name="sortbyfield" defaultValue={ 'updated_at' }>
                            <option value="updated_at">{ t("Sort by updated") }</option>
                            <option value="name">{ t("Sort by name") }</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="sortbytype">
                        <Form.Select aria-label={ t("Sort type") } name="sortbytype" defaultValue={ 'DESC' }>
                            <option value="ASC">{ t("Ascending") }</option>
                            <option value="DESC">{ t("Descending") }</option>
                        </Form.Select>
                    </Form.Group>
                    <div className="actions">
                        <Button variant="primary" type="submit">
                            { t("search") }
                        </Button>
                        <Button variant="secondary" onClick={ handleViewModeToggle } className="ms-2">
                            <i className={ viewMode === 'cards' ? 'bi bi-table' : 'bi bi-grid-3x3-gap' }></i>
                            { viewMode === 'cards' ? ' ' + t("Table view") : ' ' + t("Card view") }
                        </Button>
                    </div>
                </Form>
            </div>
            { viewMode === 'cards' ? (
                <div className="grid-container">
                    <Row>
                        { athletes.map((a: Athlete) => {
                            return <Col key={ 'athlete-' + a.id } xs={12} md={6} lg={4}>
                                <div className="athlete-card">
                                    <div className="heading">
                                        <div className="avatar">
                                            { a.avatar.length > 0 &&
                                                <img src={ a.avatar[0].url } alt={ a.name } />
                                            }
                                            { a.avatar.length == 0 &&
                                                <img src={ avatar } alt={ a.name } />
                                            }
                                        </div>
                                        <div className="meta">
                                            <div className="name">
                                                <Link to={ '/athletes/' + a.id + '/view' } title={ a.name } target="_blank">{ a.name }</Link>
                                            </div>
                                            { a.birthdate &&
                                                <div className="years">
                                                    { moment(new Date()).diff(a.birthdate, 'years') + ' ' + t("years").toLowerCase() }
                                                </div>
                                            }
                                        </div>
                                    </div>
                                    <div className="actions">
                                        {/*<a href="#" className="icon-link edit" title={ t("Edit") } onClick={ handleEdit } data-id={ a.id }>
                                            <i className="bi bi-pencil-square"></i>
                                        </a>*/}
                                        <a href="#" className="icon-link measurement" title={ t("Measurements") } onClick={ handleMeasurement } data-id={ a.id }>
                                            <i className="bi bi-clipboard-data"></i>
                                        </a>
                                        <a href="#" className="icon-link performance" title={ t("Performance") } onClick={ handlePerformance } data-id={ a.id }>
                                            <i className="bi bi-bar-chart-steps"></i>
                                        </a>
                                    </div>
                                </div>
                            </Col>
                        })}
                    </Row>
                    { hasMore &&
                        <div className="load-more-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                            <Button variant="primary" onClick={ handleLoadMore }>
                                { t("Load more") }
                            </Button>
                        </div>
                    }
                </div>
            ) : (
                <div className="table-container">
                    <div className="table-actions mb-3 d-flex align-items-center gap-3">
                        {!quickAddMode ? (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleToggleQuickAdd}
                            >
                                <i className="bi bi-plus-circle"></i>
                                {' ' + t("Add performance data")}
                            </Button>
                        ) : (
                            <>
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-0 me-2">{t("Date")}:</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={performanceDate}
                                        onChange={(e) => setPerformanceDate(e.target.value)}
                                        required
                                        style={{ width: '200px' }}
                                    />
                                </div>
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={handleSavePerformances}
                                >
                                    <i className="bi bi-save"></i>
                                    {' ' + t("Save data")}
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleCancelPerformances}
                                >
                                    <i className="bi bi-x-circle"></i>
                                    {' ' + t("Cancel")}
                                </Button>
                            </>
                        )}
                    </div>
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>{ t("Avatar") }</th>
                                <th>{ t("Name") }</th>
                                {/* Dynamic KPI columns */}
                                {getActiveKpis().map(kpi => (
                                    <th key={kpi.kpi_name} className="text-center" style={{ minWidth: '120px' }}>
                                        { getKpiLabelWithUnit(kpi.kpi_name) }
                                    </th>
                                ))}
                                <th className="text-center" style={{ width: '150px' }}>{ t("actions") }</th>
                            </tr>
                        </thead>
                        <tbody>
                            { athletes.map((a: Athlete) => {
                                const clubTeamInfo = a.registered_in?.find(r => r.current)
                                    ? `${a.registered_in.find(r => r.current)?.club_name} / ${a.registered_in.find(r => r.current)?.team_name}`
                                    : (a.teams && a.teams.length > 0 ? `${a.teams[0].club_name} / ${a.teams[0].name}` : '-');

                                return <tr key={ 'athlete-row-' + a.id }>
                                    <td>
                                        <div className="avatar-small">
                                            { a.avatar.length > 0 &&
                                                <img src={ a.avatar[0].url } alt={ a.name } />
                                            }
                                            { a.avatar.length == 0 &&
                                                <img src={ avatar } alt={ a.name } />
                                            }
                                        </div>
                                    </td>
                                    <td>
                                        <div className="athlete-name-cell">
                                            <Link to={ '/athletes/' + a.id + '/view' } title={ a.name } className="athlete-name" target="_blank">
                                                { a.name }
                                            </Link>
                                            <div className="club-team-info">
                                                { clubTeamInfo }
                                            </div>
                                        </div>
                                    </td>
                                    {/* Dynamic KPI cells */}
                                    {getActiveKpis().map(kpi => (
                                        <td key={kpi.kpi_name} className="text-center">
                                            {quickAddMode ? (
                                                kpi.data_type === 'time' ? (
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="45.494 o 01:45.494"
                                                        value={performanceInputs[a.id]?.[kpi.kpi_name] ?? ''}
                                                        onChange={(e) => handlePerformanceInputChange(a.id, kpi.kpi_name, e.target.value)}
                                                        style={{ width: '150px', margin: '0 auto', fontSize: '0.85rem' }}
                                                        title="Formatos aceptados: ss.ms, mm:ss.ms o hh:mm:ss.ms"
                                                    />
                                                ) : (
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        placeholder={kpi.unit || '0.00'}
                                                        value={performanceInputs[a.id]?.[kpi.kpi_name] ?? ''}
                                                        onChange={(e) => handlePerformanceInputChange(a.id, kpi.kpi_name, e.target.value)}
                                                        style={{ width: '100px', margin: '0 auto' }}
                                                    />
                                                )
                                            ) : (
                                                calculateAveragePerformance(a.performance, kpi.kpi_name, kpi.data_type)
                                            )}
                                        </td>
                                    ))}
                                    <td className="text-center">
                                        {!quickAddMode && (
                                            <>
                                                <a href="#" className="icon-link measurement me-2" title={ t("Measurements") } onClick={ handleMeasurement } data-id={ a.id }>
                                                    <i className="bi bi-clipboard-data"></i>
                                                </a>
                                                <a href="#" className="icon-link performance" title={ t("Performance") } onClick={ handlePerformance } data-id={ a.id }>
                                                    <i className="bi bi-bar-chart-steps"></i>
                                                </a>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            })}
                        </tbody>
                    </table>
                </div>
            )}   
            { showEdit && (selectedId != 0) &&
                <CoachAthleteEdit id={ selectedId } onSave={ handleEditOnSave } onClose={ handleEditOnClose } />
            }
            { showMeasurement && (selectedId != 0) &&
                <CoachAthleteMeasurement id={ selectedId } onSave={ handleMeasurementOnSave } onClose={ handleMeasurementOnClose } />
            }
            { showPerformance && (selectedId != 0) &&
                <CoachAthletePerformance id={ selectedId } onSave={ handlePerformanceOnSave } onClose={ handlePerformanceOnClose } />
            }
        </div>

    )
}
